import { useEffect, useState, useCallback } from 'react';
import { api } from '../services/api';

const GATEWAY_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000';
import './AdminPage.css';

interface CfgSettings {
  maxVisits?: string;
  maxPlayouts?: string;
  numSearchThreads?: string;
  ogsChatToStderr?: string;
  reportAnalysisWinratesAs?: string;
  resignThreshold?: string;
  nnCacheSizePowerOfTwo?: string;
}

interface Worker {
  worker_id: string;
  model: string;
  gtp_config: string;
  cfg_resolved_path: string;
  executable: string;
  cfg_settings: CfgSettings;
  overrides: Record<string, string>;
  last_seen: number;
  idle_ms: number;
}

interface AdminStatus {
  workers: Worker[];
  queue: { pending: number; queued: number };
  games: { total: number; active: number };
}

const CFG_LABEL: Record<string, string> = {
  maxVisits:               'Max Visits',
  maxPlayouts:             'Max Playouts',
  numSearchThreads:        'Number of Search Threads',
  ogsChatToStderr:         'Win Rate Output (ogsChatToStderr)',
  reportAnalysisWinratesAs:'Win Rate Perspective',
};

const CFG_ORDER = [
  'maxVisits', 'maxPlayouts', 'numSearchThreads', 'ogsChatToStderr',
  'reportAnalysisWinratesAs',
];

function statusBadge(value: string | undefined, field: string) {
  if (field === 'ogsChatToStderr') {
    const ok = value?.toLowerCase() === 'true';
    return <span className={`badge ${ok ? 'badge-ok' : 'badge-warn'}`}>{value ?? '—'}</span>;
  }
  return <span className="badge badge-neutral">{value ?? '—'}</span>;
}

function modelBasename(path: string) {
  return path.replace(/\\/g, '/').split('/').pop() ?? path;
}

function formatIdle(ms: number) {
  if (ms < 1000) return `${ms}ms`;
  if (ms < 60000) return `${(ms / 1000).toFixed(1)}s`;
  return `${(ms / 60000).toFixed(1)}m`;
}

export default function AdminPage() {
  const [status, setStatus] = useState<AdminStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);

  const refresh = useCallback(async () => {
    try {
      const data = await api.getAdminStatus();
      setStatus(data);
      setLastRefresh(new Date());
      setError(null);
    } catch (e: any) {
      setError(e.message ?? 'Failed to load status');
    }
  }, []);

  useEffect(() => {
    refresh();
    const interval = setInterval(refresh, 10000);
    return () => clearInterval(interval);
  }, [refresh]);

  return (
    <div className="admin-page">
      <div className="admin-header">
        <h1>DeepGo Admin</h1>
        <div className="admin-header-right">
          {lastRefresh && <span className="refresh-time">Updated {lastRefresh.toLocaleTimeString()}</span>}
          <button className="btn-refresh" onClick={refresh}>Refresh</button>
        </div>
      </div>

      {error && <div className="admin-error">{error}</div>}

      {status && (
        <>
          {/* Server status row */}
          <div className="status-cards">
            <div className="status-card">
              <div className="status-card-value">{status.workers.length}</div>
              <div className="status-card-label">Workers Online</div>
            </div>
            <div className="status-card">
              <div className="status-card-value">{status.queue.pending}</div>
              <div className="status-card-label">Active Requests</div>
            </div>
            <div className="status-card">
              <div className="status-card-value">{status.queue.queued}</div>
              <div className="status-card-label">Queued Jobs</div>
            </div>
            <div className="status-card">
              <div className="status-card-value">{status.games.active}</div>
              <div className="status-card-label">Active Games (10m)</div>
            </div>
            <div className="status-card">
              <div className="status-card-value">{status.games.total}</div>
              <div className="status-card-label">Total Games</div>
            </div>
          </div>

          {/* Workers */}
          <div className="section-header">
            <h2>Workers</h2>
            <span className="gateway-url">Gateway: <code>{GATEWAY_URL}</code></span>
          </div>
          {status.workers.length === 0 ? (
            <div className="admin-empty">No workers connected.</div>
          ) : (
            status.workers.map(w => (
              <div key={w.worker_id} className="worker-card">
                <div className="worker-title">
                  <span className="dot-online" />
                  Worker <code>{w.worker_id}</code>
                  <span className="worker-idle">idle {formatIdle(w.idle_ms)}</span>
                </div>

                <table className="config-table">
                  <tbody>
                    <tr>
                      <td>Model</td>
                      <td><code title={w.model}>{modelBasename(w.model)}</code></td>
                    </tr>
                    <tr>
                      <td>Config File</td>
                      <td>
                        <code title={w.cfg_resolved_path || w.gtp_config}>
                          {w.cfg_resolved_path || w.gtp_config}
                        </code>
                      </td>
                    </tr>
                    <tr>
                      <td>Executable</td>
                      <td><code title={w.executable}>{w.executable}</code></td>
                    </tr>

                    {CFG_ORDER.map(key => {
                      const overrideVal = w.overrides[key];
                      const cfgVal = w.cfg_settings[key as keyof CfgSettings];
                      const effectiveVal = overrideVal ?? cfgVal;
                      return (
                        <tr key={key}>
                          <td>{CFG_LABEL[key]}</td>
                          <td>
                            {statusBadge(effectiveVal, key)}
                            {overrideVal && (
                              <span className="override-note" title={`cfg default: ${cfgVal ?? 'not set'}`}>
                                overridden
                              </span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ))
          )}
        </>
      )}
    </div>
  );
}
